import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Mail, Lock } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { GoogleLoginButton } from '@/components/auth/GoogleLoginButton'
import { useAuth } from '@/contexts/AuthContext'
import { useFormTracking } from '@/hooks/useAnalytics'
import { loginSchema, type LoginFormData } from '@/utils/validation'

interface LoginFormProps {
  onSwitchToSignUp?: () => void
  onForgotPassword?: () => void
}

export function LoginForm({ onSwitchToSignUp, onForgotPassword }: LoginFormProps) {
  const { login, loginWithGoogle, isLoading, error } = useAuth()
  const { trackFormStart, trackFormSubmit, trackFormError } = useFormTracking('login-form', 'authentication')

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    trackFormSubmit({ auth_method: 'email' });

    try {
      await login(data)
    } catch (error) {
      // Track form-specific error
      trackFormError('login_failed', (error as Error).message);
      console.error('Login failed:', error)
    }
  }

  const handleGoogleLogin = async () => {
    try {
      await loginWithGoogle()
    } catch (error) {
      // Error is handled by the auth context
      console.error('Google login failed:', error)
    }
  }

  return (
    <div className="w-full max-w-md space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome Back
        </h1>
        <p className="text-gray-600">
          Sign in to your Real Estate Analysis account
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg animate-slide-up">
          <p className="text-sm text-red-600 text-center">{error}</p>
        </div>
      )}

      {/* Google Login */}
      <GoogleLoginButton
        onClick={handleGoogleLogin}
        isLoading={isLoading}
        disabled={isSubmitting}
      />

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">Or continue with email</span>
        </div>
      </div>

      {/* Email/Password Form */}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4"
        onFocus={() => trackFormStart()}
      >
        <Input
          label="Email address"
          type="email"
          placeholder="Enter your email"
          icon={<Mail size={20} />}
          error={errors.email?.message}
          {...register('email')}
        />

        <Input
          label="Password"
          type="password"
          placeholder="Enter your password"
          icon={<Lock size={20} />}
          error={errors.password?.message}
          {...register('password')}
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
              Remember me
            </label>
          </div>

          <button
            type="button"
            onClick={onForgotPassword}
            className="text-sm font-medium text-primary-600 hover:text-primary-500 focus:outline-none focus:underline transition-colors"
          >
            Forgot password?
          </button>
        </div>

        <Button
          type="submit"
          className="w-full"
          isLoading={isSubmitting}
          disabled={isLoading}
        >
          Sign In
        </Button>
      </form>

      {/* Sign Up Link */}
      <div className="text-center">
        <p className="text-sm text-gray-600">
          Don't have an account?{' '}
          <button
            onClick={onSwitchToSignUp}
            className="font-medium text-primary-600 hover:text-primary-500 focus:outline-none focus:underline transition-colors"
          >
            Sign up for free
          </button>
        </p>
      </div>

      {/* Investment Tip of the Day - Hidden on mobile */}
      <div className="hidden sm:block mt-8 p-6 bg-primary-600 bg-opacity-10 rounded-lg">
        <p className="text-primary-900 font-medium">
          💡 Investment Tip of the Day
        </p>
        <p className="text-primary-800 mt-2">
          "Real estate cannot be lost or stolen, nor can it be carried away. 
          Purchased with common sense, paid for in full, and managed with reasonable care, 
          it is about the safest investment in the world." - Franklin D. Roosevelt
        </p>
      </div>
    </div>
  )
}